/**
 * The market's price points, as the registry stores them — `markdown.price_point_rule` (bata-reqs
 * issue 38 R4, bata-platform ADR 6190, `docs/adr/9067`).
 *
 * The rule is endings per price band, in one compact text the platform parses with
 * `planning.markdown.price_points.parse_price_point_rule`:
 *
 *     4900:1000:900;99900:5000:4900;*:10000:9900
 *
 * bands separated by `;`, each `<up to shelf price>:<step>:<ending>`, the last band's edge `*` (no
 * upper edge). A band's price points are `k × step + ending` above the previous band's edge and up
 * to its own: up to 4,900 every 1,000 ending in 900; up to 99,900 every 5,000 ending in 4,900;
 * above it every 10,000 ending in 9,900.
 *
 * This module mirrors the platform's check reason for reason (`RULE_REASONS`), so the editor names
 * the same defect the platform would refuse the proposal for, before it is sent. The platform stays
 * the authority: a refusal it returns (`parameter_value_refused`, `reason = malformed`,
 * `ruleReason`) is shown by its own reason.
 *
 * Exact arithmetic: every figure is held as an integer count of the smallest decimal place any
 * figure in the rule uses, so `0.99` endings compare without floating-point error.
 *
 * No React, no `window`, no network.
 */

/** The catalogue unit the platform's card gives the key (`parameter_cards.py`). */
export const PRICE_POINT_RULE_UNIT = 'price_point_rule';

export const BAND_SEPARATOR = ';';
export const FIELD_SEPARATOR = ':';
export const OPEN_EDGE = '*';

/** The platform's reasons, verbatim (`planning.markdown.price_points.RULE_REASONS`). */
export const RULE_REASONS = [
  'empty',
  'band_malformed',
  'band_edge_not_positive',
  'band_edges_not_ascending',
  'last_band_not_open',
  'open_band_not_last',
  'step_not_positive',
  'ending_out_of_range',
  'band_without_price_point',
  'off_minor_unit',
] as const;

export type RuleReason = (typeof RULE_REASONS)[number];

/** One band as typed: decimal text, the edge `null` on the open last band. */
export interface PricePointBand {
  readonly upTo: string | null;
  readonly step: string;
  readonly ending: string;
}

export type RuleCheck =
  | { readonly ok: true; readonly bands: readonly PricePointBand[]; readonly text: string }
  | { readonly ok: false; readonly reason: RuleReason; readonly band: number | null };

const DECIMAL_TEXT = /^\d+(?:\.\d+)?$/;

function places(text: string): number {
  const point = text.indexOf('.');
  return point < 0 ? 0 : text.length - point - 1;
}

/** `text` as an integer count of `10^-scale`; exact for decimal text. */
function scaled(text: string, scale: number): number {
  const [whole = '0', fraction = ''] = text.split('.');
  return Number(whole + fraction.padEnd(scale, '0'));
}

/** A decimal as the rule spells it: no leading zeros, no trailing fraction zeros. */
function canonicalNumber(text: string): string {
  const [rawWhole = '0', rawFraction = ''] = text.split('.');
  const whole = rawWhole.replace(/^0+(?=\d)/, '');
  const fraction = rawFraction.replace(/0+$/, '');
  return fraction === '' ? whole : `${whole}.${fraction}`;
}

/** The rule's text, canonical: what `parsePricePointRule` reads back unchanged. */
export function formatPricePointRule(bands: readonly PricePointBand[]): string {
  return bands
    .map((band) => [band.upTo === null ? OPEN_EDGE : canonicalNumber(band.upTo), canonicalNumber(band.step), canonicalNumber(band.ending)]
      .join(FIELD_SEPARATOR))
    .join(BAND_SEPARATOR);
}

const refused = (reason: RuleReason, band: number | null = null): RuleCheck => ({ ok: false, reason, band });

/**
 * Parse and check a rule, as the platform does: the first defect, by its reason and the band it is
 * in (0-based; `null` for the rule as a whole). Whitespace around fields is ignored, as there.
 */
export function parsePricePointRule(text: string | null | undefined): RuleCheck {
  if (text === null || text === undefined || text.trim() === '') return refused('empty');
  const parts = text.split(BAND_SEPARATOR).map((part) => part.trim());
  const bands: PricePointBand[] = [];
  for (const [index, part] of parts.entries()) {
    const fields = part.split(FIELD_SEPARATOR).map((field) => field.trim());
    if (fields.length !== 3) return refused('band_malformed', index);
    const [edge = '', step = '', ending = ''] = fields;
    const open = edge === OPEN_EDGE;
    if ((!open && !DECIMAL_TEXT.test(edge)) || !DECIMAL_TEXT.test(step) || !DECIMAL_TEXT.test(ending)) {
      return refused('band_malformed', index);
    }
    bands.push({ upTo: open ? null : edge, step, ending });
  }
  return checkBands(bands);
}

/** Check bands already split into fields (the editor's table). */
export function checkBands(bands: readonly PricePointBand[]): RuleCheck {
  if (bands.length === 0) return refused('empty');
  for (const [index, band] of bands.entries()) {
    const figures = [band.step, band.ending, ...(band.upTo === null ? [] : [band.upTo])];
    if (figures.some((figure) => !DECIMAL_TEXT.test(figure.trim()))) return refused('band_malformed', index);
  }
  const scale = Math.max(...bands.flatMap((band) => [band.step, band.ending, band.upTo ?? '0'].map((figure) => places(figure.trim()))));
  let previous: number | null = null;
  for (const [index, band] of bands.entries()) {
    const last = index === bands.length - 1;
    if (band.upTo === null && !last) return refused('open_band_not_last', index);
    if (band.upTo !== null && last) return refused('last_band_not_open', index);
    const upTo = band.upTo === null ? null : scaled(band.upTo.trim(), scale);
    const step = scaled(band.step.trim(), scale);
    const ending = scaled(band.ending.trim(), scale);
    if (upTo !== null && upTo <= 0) return refused('band_edge_not_positive', index);
    if (step <= 0) return refused('step_not_positive', index);
    if (ending < 0 || ending >= step) return refused('ending_out_of_range', index);
    if (upTo !== null && previous !== null && upTo <= previous) return refused('band_edges_not_ascending', index);
    const lower = previous ?? 0;
    if (upTo !== null && firstPointAbove(lower, step, ending) > upTo) return refused('band_without_price_point', index);
    if (upTo !== null) previous = upTo;
  }
  const canonical = bands.map((band) => ({
    upTo: band.upTo === null ? null : canonicalNumber(band.upTo.trim()),
    step: canonicalNumber(band.step.trim()),
    ending: canonicalNumber(band.ending.trim()),
  }));
  return { ok: true, bands: canonical, text: formatPricePointRule(canonical) };
}

/** The lowest `k × step + ending` strictly above `lower` (all scaled integers). */
function firstPointAbove(lower: number, step: number, ending: number): number {
  let point = Math.max(0, Math.ceil((lower - ending) / step)) * step + ending;
  while (point <= lower || point <= 0) point += step;
  return point;
}

/**
 * The first few price points of each band, for the editor to show what a band means: at most
 * `count` per band, as numbers in the rule's own units. Empty for a band with none.
 */
export function samplePoints(bands: readonly PricePointBand[], count: number): readonly (readonly number[])[] {
  const scale = Math.max(0, ...bands.flatMap((band) => [band.step, band.ending, band.upTo ?? '0'].map((figure) => places(figure.trim()))));
  const unit = 10 ** scale;
  let lower = 0;
  return bands.map((band) => {
    const step = scaled(band.step.trim(), scale);
    const ending = scaled(band.ending.trim(), scale);
    const upTo = band.upTo === null ? null : scaled(band.upTo.trim(), scale);
    const points: number[] = [];
    if (step > 0 && ending >= 0 && ending < step) {
      let point = firstPointAbove(lower, step, ending);
      while (points.length < count && (upTo === null || point <= upTo)) {
        points.push(point / unit);
        point += step;
      }
    }
    if (upTo !== null) lower = upTo;
    return points;
  });
}

/** Each reason's sentence, spelled out for the catalogue check. */
export const RULE_REASON_KEY: Readonly<Record<RuleReason, string>> = {
  empty: 'parameterEdit.pricePointRule.reason.empty',
  band_malformed: 'parameterEdit.pricePointRule.reason.bandMalformed',
  band_edge_not_positive: 'parameterEdit.pricePointRule.reason.bandEdgeNotPositive',
  band_edges_not_ascending: 'parameterEdit.pricePointRule.reason.bandEdgesNotAscending',
  last_band_not_open: 'parameterEdit.pricePointRule.reason.lastBandNotOpen',
  open_band_not_last: 'parameterEdit.pricePointRule.reason.openBandNotLast',
  step_not_positive: 'parameterEdit.pricePointRule.reason.stepNotPositive',
  ending_out_of_range: 'parameterEdit.pricePointRule.reason.endingOutOfRange',
  band_without_price_point: 'parameterEdit.pricePointRule.reason.bandWithoutPricePoint',
  off_minor_unit: 'parameterEdit.pricePointRule.reason.offMinorUnit',
};

/** A reason the platform returned, or `null` for a word this build does not know. */
export function ruleReason(value: unknown): RuleReason | null {
  return typeof value === 'string' && (RULE_REASONS as readonly string[]).includes(value) ? (value as RuleReason) : null;
}

// ── the rounding mode, and how a step's price got where it is ─────────────────

/** `markdown.price_rounding_mode`'s vocabulary (bata-platform `ROUNDING_MODES`), in the ruling's order. */
export const ROUNDING_MODES = ['percent', 'nearest_price_point', 'percent_then_down_to_price_point'] as const;

export type RoundingMode = (typeof ROUNDING_MODES)[number];

/** Each mode in words, spelled out for the catalogue check. */
export const ROUNDING_MODE_LABEL_KEY: Readonly<Record<RoundingMode, string>> = {
  percent: 'parameterEdit.roundingMode.percent',
  nearest_price_point: 'parameterEdit.roundingMode.nearestPricePoint',
  percent_then_down_to_price_point: 'parameterEdit.roundingMode.percentThenDown',
};

/** A mode's label key, or `null` for a word this build does not know (then shown by its code). */
export function roundingModeLabelKey(mode: string | null | undefined): string | null {
  return mode !== null && mode !== undefined && (ROUNDING_MODES as readonly string[]).includes(mode)
    ? ROUNDING_MODE_LABEL_KEY[mode as RoundingMode]
    : null;
}

/**
 * How a step's price reached its shelf price (bata-platform `price_points.DIRECTIONS`, plus the
 * outlet recommendation's `last_zona_sales_price`), spelled out for the catalogue check.
 */
export const ROUNDING_DIRECTION_LABEL_KEY: Readonly<Record<string, string>> = {
  currency: 'parameterEdit.roundingDirection.currency',
  nearest: 'parameterEdit.roundingDirection.nearest',
  down: 'parameterEdit.roundingDirection.down',
  up_over_cap: 'parameterEdit.roundingDirection.upOverCap',
  up_no_point_below: 'parameterEdit.roundingDirection.upNoPointBelow',
  below_original: 'parameterEdit.roundingDirection.belowOriginal',
  last_zona_sales_price: 'parameterEdit.roundingDirection.lastZonaSalesPrice',
};